import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDialog } from '../../context/DialogContext';
import { SoftSurface, RING } from '../surface';
import LoadingSpinner from '../LoadingSpinner';
import useContainerWidth from '../../hooks/useContainerWidth';
import { resolveScheme, schemeWarning, normalizeHex, contrast, bandBg, bandFg } from '../../utils/emailTheme';
import SendPanel from './SendPanel';
import AudiencePicker, { emptyAudience } from './AudiencePicker';
import EmailPreview from './EmailPreview';
import SendingFrom from './SendingFrom';
import { SoftField, SoftText, toLocalInputValue } from './ui';
import { MoreMenu } from '../admin/shell/ui';
import { StudioCard, Segmented, ICON as STUDIO_ICON, pillOf } from './studioUi';
import {
  BLOCK_TYPES, ADD_ORDER, STARTERS, buildStarter, emptyBlock, newKey, toSection, fromSection, needsAlt, excerptOf,
  wordCount, optimisticChecks,
} from './emailBlocks';

export { default as EmailPreview } from './EmailPreview';

/**
 * Email Studio — the one email builder (design_handoff_email_studio). Three homes, one component:
 * the admin newsletter (`mode="admin"`, template C, staff verbs) and a club's or Pro page's email
 * (`mode="page"`, template D). Four steps — 01 DESIGN (starters, saved templates, the page's colour
 * schemes), 02 WRITE (inbox line, top card, blocks, bottom button), 03 WHO GETS IT, 04 SEND — beside
 * a sticky live preview that draws the email the way the templates do and opens any block you click.
 *
 * `client` is the caller's API: get / save / sendTest / send / schedule / unschedule / duplicate /
 * uploadImage, plus listTemplates / saveTemplate / deleteTemplate / listEvents, and for a page
 * listSchemes / saveScheme / deleteScheme / getPalette / replaceLogo. `perms` is
 * { canEdit, canCreate, canSend }. The Before-Send checklist is the server's (`checks`); while the
 * draft is dirty the same five rows are mirrored locally so they move as you type.
 *
 * On a phone (`narrow`, under 640px of its own width) the studio is one column: an EDIT · PREVIEW
 * switch under the hero decides which column is on screen (a preview under a long editor was
 * never reached), the hero keeps REVIEW & SEND and puts the other two behind a ⋯, a block's
 * move / duplicate / delete go behind a ⋯ too, the cards lose their 24px padding, and the
 * preview opens on PHONE. Tapping a block in the preview flips back to the editor on it.
 */
const NarrowCtx = createContext(false);

const STEPS = ['DESIGN', 'WRITE', 'WHO GETS IT', 'SEND'];
const NEXT_LABEL = ['NEXT: WRITE →', 'NEXT: WHO GETS IT →', 'NEXT: SEND →'];
const ICON = STUDIO_ICON;
const EMPTY_META = { subject: '', preview: '', issueLabel: '', title: '', intro: '', ctaUrl: '', ctaText: '' };
const PILL = 'inline-flex items-center justify-center rounded-full font-mono font-extrabold uppercase transition-colors duration-200 focus:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#000] disabled:cursor-not-allowed disabled:opacity-60';
const clock = (iso) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toUpperCase();

export default function EmailBuilder(props) {
  return <SoftSurface><Studio {...props} /></SoftSurface>;
}

function Studio({
  id, client, perms, mode = 'admin', page = null, onSavedNew, onState, testTarget = 'your inbox', onBack, roleTag,
}) {
  const { showAlert, showConfirm } = useDialog();
  const { canEdit, canCreate } = perms;
  const isNew = !id || id === 'new';
  const isPage = mode === 'page';
  const rootRef = useRef(null);
  const width = useContainerWidth(rootRef);
  const wide = width >= 1080;
  const narrow = width < 640;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(EMPTY_META);
  const [blocks, setBlocks] = useState([]);
  const [schemeId, setSchemeId] = useState(null);
  const [showName, setShowName] = useState(true);
  const [audience, setAudience] = useState(isPage ? { kind: 'followers', audience: emptyAudience(), picked: [] } : { kind: 'everyone', regionIds: [] });
  const [reach, setReach] = useState(null);
  const [status, setStatus] = useState('Draft');
  const [serverChecks, setServerChecks] = useState(null);
  const [serverCanSend, setServerCanSend] = useState(false);
  const [testSentAt, setTestSentAt] = useState(null);
  const [scheduledFor, setScheduledFor] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);
  const [scheduleInput, setScheduleInput] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [step, setStep] = useState(0);
  const [activeKey, setActiveKey] = useState(null);
  const [view, setView] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 640 ? 'phone' : 'desktop'));
  // Which column a one-column layout shows: the editor or the preview.
  const [pane, setPane] = useState('edit');
  const [starter, setStarter] = useState(null);
  const [events, setEvents] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [palette, setPalette] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [logoUrl, setLogoUrl] = useState(page?.logoUrl || null);
  const [recentImages, setRecentImages] = useState([]);
  const seeded = useRef(false);
  const blocksTouched = useRef(false);

  const isLocked = status === 'Sending' || status === 'Sent' || status === 'Failed' || status === 'Paused';
  const isScheduled = status === 'Scheduled';
  const readOnly = !canEdit || isLocked;

  useEffect(() => {
    onState?.({ status, issueLabel: meta.issueLabel, subject: meta.subject, dirty, updatedAt, loading });
  }, [onState, status, meta.issueLabel, meta.subject, dirty, updatedAt, loading]);

  // ---- Loading ------------------------------------------------------------

  const loadOne = useCallback(async (targetId) => {
    const r = await client.get(targetId);
    if (!r?.success) return;
    const n = r.data.newsletter;
    setMeta({
      subject: n.subject || '', preview: n.preview || '', issueLabel: n.issueLabel || '',
      title: n.title || '', intro: n.intro || '', ctaUrl: n.ctaUrl || '', ctaText: n.ctaText || '',
    });
    setBlocks((n.sections || []).map(fromSection));
    setSchemeId(n.schemeId || null);
    setShowName(n.showBrandName !== false);
    if (isPage && n.audienceKind) {
      setAudience((prev) => ({
        kind: n.audienceKind,
        audience: { userIds: n.audience?.userIds || [], contactIds: n.audience?.contactIds || [], groupIds: n.audience?.groupIds || [] },
        picked: prev.picked.filter((p) => (p.kind === 'user' ? n.audience?.userIds : n.audience?.contactIds)?.includes(p.id)),
      }));
    } else if (!isPage) {
      setAudience({ kind: n.audienceKind || 'everyone', regionIds: n.audience?.regionIds || [] });
    }
    setStatus(n.status);
    setTestSentAt(n.testSentAt);
    setScheduledFor(n.scheduledFor || null);
    setScheduleError(n.scheduleError || null);
    setScheduleInput(n.scheduledFor ? toLocalInputValue(n.scheduledFor) : '');
    setUpdatedAt(n.updatedAt || n.createdAt || null);
    setServerChecks(r.data.checks || null);
    setServerCanSend(!!r.data.canSend);
    if (r.data.reach) setReach(r.data.reach);
    setDirty(false);
    seeded.current = true;
  }, [client, isPage]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (!isNew) await loadOne(id);
      setLoading(false);
    })();
  }, [id, isNew, loadOne]);

  useEffect(() => {
    let live = true;
    client.listEvents?.().then((r) => { if (live && r?.success) setEvents(r.data || []); });
    client.listTemplates?.().then((r) => { if (live && r?.success) setTemplates(r.data || []); });
    if (isPage) {
      client.listSchemes?.().then((r) => { if (live && r?.success) setSchemes(r.data || []); });
      client.getPalette?.().then((r) => { if (live && r?.success) setPalette(r.data || []); });
    }
    return () => { live = false; };
  }, [client, isPage]);

  // A new email opens on its starter — pages on EVENT PROMO, the admin on NEWSLETTER — once the
  // event list is in, so the EVENT block lands on the page's next event.
  useEffect(() => {
    if (!isNew || seeded.current || loading) return;
    const sid = isPage ? 'event' : 'news';
    setBlocks(buildStarter(sid, events));
    setStarter(sid);
    seeded.current = true;
  }, [isNew, isPage, loading, events]);
  // …and if the events arrive after it was seeded, fill the empty EVENT blocks once.
  useEffect(() => {
    if (!events.length || blocksTouched.current) return;
    setBlocks((prev) => (prev.some((b) => b.type === 'event' && !b.eventId)
      ? prev.map((b, i) => (b.type === 'event' && !b.eventId ? { ...b, eventId: (events[i === prev.length - 1 && events[1] ? 1 : 0] || events[0]).id } : b))
      : prev));
  }, [events]);

  const eventMap = useMemo(() => Object.fromEntries(events.map((e) => [e.id, e])), [events]);
  const scheme = isPage && schemeId ? schemes.find((s) => s.id === schemeId) || null : null;
  const theme = resolveScheme(scheme);

  // ---- Edits ---------------------------------------------------------------

  const touch = () => setDirty(true);
  const setMetaField = (k, v) => { setMeta((m) => ({ ...m, [k]: v })); touch(); };
  const editBlocks = (fn) => { blocksTouched.current = true; setBlocks(fn); touch(); };
  const setBlock = (key, patch) => editBlocks((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  const addBlock = (type) => {
    const b = emptyBlock(type, blocks);
    if (type === 'event' && events[0]) b.eventId = events[0].id;
    editBlocks((prev) => [...prev, b]);
    setActiveKey(b.key);
  };
  const moveBlock = (i, d) => editBlocks((prev) => {
    const j = i + d;
    if (j < 0 || j >= prev.length) return prev;
    const c = [...prev]; [c[i], c[j]] = [c[j], c[i]]; return c;
  });
  const duplicateBlock = (i) => editBlocks((prev) => { const c = [...prev]; c.splice(i + 1, 0, { ...prev[i], key: newKey() }); return c; });
  const deleteBlock = async (key) => {
    const ok = await showConfirm({ title: 'DELETE BLOCK', message: 'This block will be removed from the email.', confirmLabel: 'DELETE', cancelLabel: 'KEEP', variant: 'danger' });
    if (!ok) return;
    editBlocks((prev) => prev.filter((b) => b.key !== key));
    if (activeKey === key) setActiveKey(null);
  };

  const pickStarter = async (sid) => {
    if (readOnly) return;
    if (blocksTouched.current || (!isNew && blocks.length > 0)) {
      const name = STARTERS.find((s) => s.id === sid)?.name;
      const ok = await showConfirm({ title: 'REPLACE YOUR BLOCKS', message: `Replace your blocks with ${name}?`, confirmLabel: 'REPLACE', cancelLabel: 'KEEP MINE', variant: 'danger' });
      if (!ok) return;
    }
    setBlocks(buildStarter(sid, events));
    blocksTouched.current = false;
    setStarter(sid);
    setActiveKey(null);
    touch();
  };

  const applyTemplate = async (t) => {
    if (readOnly) return;
    if (blocks.length > 0) {
      const ok = await showConfirm({ title: 'REPLACE YOUR BLOCKS', message: `Replace your blocks with ${t.name}?`, confirmLabel: 'REPLACE', cancelLabel: 'KEEP MINE', variant: 'danger' });
      if (!ok) return;
    }
    setBlocks((Array.isArray(t.blocks) ? t.blocks : []).map((b) => ({ ...b, key: newKey() })));
    if (isPage) setSchemeId(t.schemeId && schemes.some((s) => s.id === t.schemeId) ? t.schemeId : null);
    setStarter(null);
    blocksTouched.current = true;
    touch();
  };

  // ---- Schemes (a page's own, max 3; saved as you edit) --------------------

  const schemeTimers = useRef({});
  const editScheme = (sid, patch) => {
    setSchemes((prev) => prev.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
    touch();
    clearTimeout(schemeTimers.current[sid]);
    schemeTimers.current[sid] = setTimeout(async () => {
      const cur = { ...schemes.find((s) => s.id === sid), ...patch };
      if (!normalizeHex(cur.primary) || !normalizeHex(cur.accent) || !normalizeHex(cur.canvas)) return;
      const r = await client.saveScheme({ id: sid, name: cur.name, primary: cur.primary, accent: cur.accent, canvas: cur.canvas });
      if (!r?.success) await showAlert({ title: 'COULD NOT SAVE THE SCHEME', message: r?.message || r?.error || 'Try again.', type: 'error' });
    }, 600);
  };
  const addScheme = async () => {
    const p = palette.length ? palette : ['#F2C230', '#1C1712'];
    const r = await client.saveScheme({ name: `BRAND ${schemes.length + 1}`, primary: p[1] || '#1C1712', accent: p[0] || '#F2C230', canvas: '#FFFFFF' });
    if (!r?.success) { await showAlert({ title: 'COULD NOT ADD A SCHEME', message: r?.message || r?.error || 'Try again.', type: 'error' }); return; }
    setSchemes((prev) => [...prev, r.data]);
    setSchemeId(r.data.id);
    touch();
  };
  const removeScheme = async (s) => {
    const ok = await showConfirm({ title: 'DELETE SCHEME', message: `Delete ${s.name}? Emails in it go back to PORTAL.`, confirmLabel: 'DELETE', cancelLabel: 'KEEP', variant: 'danger' });
    if (!ok) return;
    const r = await client.deleteScheme(s.id);
    if (!r?.success) { await showAlert({ title: 'COULD NOT DELETE', message: r?.message || r?.error || 'Try again.', type: 'error' }); return; }
    setSchemes((prev) => prev.filter((x) => x.id !== s.id));
    setSchemeId(null);
    touch();
  };
  const replaceLogo = async (file) => {
    if (!file) return;
    const r = await client.replaceLogo(file);
    if (!r?.success) { await showAlert({ title: 'COULD NOT REPLACE THE LOGO', message: r?.message || r?.error || 'Try again.', type: 'error' }); return; }
    setLogoUrl(r.data.logoUrl || null);
    client.getPalette?.().then((p) => { if (p?.success) setPalette(p.data || []); });
  };

  // ---- Persistence ---------------------------------------------------------

  const save = async () => {
    setSaving(true);
    const payload = {
      id: isNew ? undefined : id,
      ...meta,
      sections: blocks.map(toSection),
      ...(isPage
        ? { channel: 'email', audienceKind: audience.kind, audience: audience.audience, schemeId: schemeId || null, showBrandName: showName }
        : { audienceKind: audience.kind, audience: { regionIds: audience.regionIds || [] } }),
    };
    const r = await client.save(payload);
    setSaving(false);
    if (!r?.success) {
      await showAlert({ title: 'COULD NOT SAVE', message: r?.message || r?.error || 'Try again.', type: 'error' });
      return null;
    }
    if (isNew) onSavedNew?.(r.data.id);
    else await loadOne(id);
    return r.data.id;
  };

  const sendTest = async () => {
    // A test of unsaved copy proves nothing, and the server invalidates the stamp on every edit.
    const savedId = await save();
    const target = savedId || id;
    if (!target) return;
    const r = await client.sendTest(target);
    if (r?.success) {
      if (!isNew) await loadOne(target);
      await showAlert({ title: 'TEST SENT', message: `Check ${testTarget}. Look at it on a phone before you send.`, type: 'success' });
    } else {
      await showAlert({ title: 'TEST FAILED', message: r?.message || r?.error || 'Try again.', type: 'error' });
    }
  };

  const doSchedule = async () => {
    if (!scheduleInput) return;
    const iso = new Date(scheduleInput).toISOString();
    const r = await client.schedule(id, iso);
    if (r?.success) {
      await loadOne(id);
      await showAlert({ title: 'SCHEDULED', message: `It goes out ${new Date(iso).toLocaleString()}. You can keep editing until then, but any edit needs a fresh test or it won't fire.`, type: 'success' });
    } else {
      await showAlert({ title: 'COULD NOT SCHEDULE', message: r?.message || r?.error || 'Try again.', type: 'error' });
    }
  };
  const doUnschedule = async () => {
    const r = await client.unschedule(id);
    if (r?.success) await loadOne(id);
    else await showAlert({ title: 'COULD NOT UNSCHEDULE', message: r?.message || r?.error || 'Try again.', type: 'error' });
  };
  const doDuplicate = async () => {
    const r = await client.duplicate(id);
    if (r?.success) onSavedNew?.(r.data.id);
    else await showAlert({ title: 'COULD NOT DUPLICATE', message: r?.message || r?.error || 'Try again.', type: 'error' });
  };

  const count = reach?.count ?? 0;
  const noun = isPage ? (count === 1 ? 'person' : 'people') : (count === 1 ? 'user' : 'users');
  const doSend = async () => {
    const n = count.toLocaleString();
    const ok = await showConfirm({
      title: 'SEND THIS EMAIL',
      message: `This sends "${meta.subject || 'this email'}" to ${n} ${noun}. It cannot be recalled, and the email becomes read-only.`,
      confirmLabel: `SEND TO ${n} ${noun.toUpperCase()}`, cancelLabel: 'CANCEL', variant: 'primary',
    });
    if (!ok) return;
    const r = await client.send(id);
    if (r?.success) {
      await loadOne(id);
      await showAlert({ title: 'ON ITS WAY', message: `Queued for ${r.data.queued} ${r.data.queued === 1 ? 'recipient' : 'recipients'}. Delivery runs in the background.`, type: 'success' });
    } else {
      await showAlert({ title: 'COULD NOT SEND', message: r?.message || r?.error || 'Try again.', type: 'error' });
    }
  };

  const saveAsTemplate = async () => {
    if (!client.saveTemplate) return;
    const name = (meta.subject || 'UNTITLED').toUpperCase().slice(0, 24);
    const r = await client.saveTemplate({ name, blocks: blocks.map(({ key, ...b }) => b), schemeId: isPage ? schemeId : null });
    if (!r?.success) { await showAlert({ title: 'COULD NOT SAVE THE TEMPLATE', message: r?.message || r?.error || 'Try again.', type: 'error' }); return; }
    setTemplates((prev) => [r.data, ...prev]);
    setStep(0);
  };

  // ---- Derived -------------------------------------------------------------

  const testFresh = !!testSentAt && !dirty && (!updatedAt || new Date(testSentAt) >= new Date(updatedAt));
  const checks = useMemo(() => {
    if (dirty || isNew || !serverChecks) return optimisticChecks(meta, blocks);
    return serverChecks;
  }, [dirty, isNew, serverChecks, meta, blocks]);
  const writeOk = checks.filter((c) => ['subject', 'preview', 'blocks', 'alt'].includes(c.key)).every((c) => c.ok);
  const stepDone = [true, writeOk, count > 0, status === 'Sent'];
  const totalWords = wordCount(meta, blocks);

  const onFix = (c) => {
    if (c.key === 'test') { sendTest(); return; }
    setStep(c.step === 2 ? 2 : 1);
  };
  const selectFromPreview = (key) => { setActiveKey(key); setStep(1); setPane('edit'); };

  if (loading) return <div ref={rootRef} className="p-6"><LoadingSpinner /></div>;

  const previewCol = wide ? Math.min(600, Math.max(460, Math.round(width * 0.42))) : width;
  const phone = view === 'phone';
  const zoom = Math.min(1, (previewCol - 28) / (phone ? 375 : 600));
  const eyebrow = isPage ? `${page?.kind === 'pro' || page?.ownerType === 'pro' ? 'PRO PAGE' : 'CLUB'} EMAIL · ${(page?.name || '').toUpperCase()}` : 'NEWSLETTER · SUPER ADMIN';
  const statusUp = String(status || 'Draft').toUpperCase();
  const savedLine = `${isNew ? 'NOT SAVED YET' : dirty ? 'UNSAVED CHANGES' : updatedAt ? `SAVED ${clock(updatedAt)}` : ''}${testFresh ? ' · TEST SENT' : ''}`;
  const previewPage = isPage ? { name: page?.name || '', logoUrl, address: page?.address } : null;

  const header = (
    <header className="relative flex flex-wrap items-end justify-between overflow-hidden font-mono text-white" style={{ gap: '16px 20px', padding: narrow ? '22px 20px' : '26px 30px', borderRadius: 28, background: '#0A0A0A' }} data-testid="studio-header">
      <span aria-hidden="true" className="absolute left-0 right-0 top-0" style={{ height: 3, background: RING }} />
      <div className="flex min-w-0 items-center" style={{ flex: '1 1 320px', gap: 16 }}>
        <span aria-hidden="true" className="flex-none overflow-hidden" style={{ width: 56, height: 56, borderRadius: 16, background: '#1F1F1F' }}>
          {isPage
            ? (logoUrl && <img src={logoUrl} alt="" className="h-full w-full object-cover" />)
            : <img src="/portal-mark-180.png" alt="" className="h-full w-full object-contain" style={{ padding: 6 }} />}
        </span>
        <div className="flex min-w-0 flex-col" style={{ gap: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.2em', color: '#8A8A8A' }}>{eyebrow}</span>
          <h1 style={{ margin: 0, fontSize: narrow ? 24 : 32, fontWeight: 700, letterSpacing: '.02em', lineHeight: 1.05, overflowWrap: 'anywhere' }}>{meta.subject || 'Untitled email'}</h1>
          <div className="flex flex-wrap items-center" style={{ gap: 8, fontSize: 10, letterSpacing: '.14em', color: '#BDBDBD' }}>
            <span data-testid="studio-status" className="flex items-center" style={{ height: 22, padding: '0 10px', borderRadius: 999, fontWeight: 800, background: status === 'Sent' ? '#22C55E' : isScheduled ? '#22ECFB' : status === 'Paused' ? '#EAB308' : '#2E2E2E', color: status === 'Sent' || isScheduled || status === 'Paused' ? '#000' : '#fff' }}>{statusUp}</span>
            <span data-testid="studio-saved">{savedLine}</span>
          </div>
        </div>
      </div>
      <div className={`flex ${narrow ? 'w-full items-center' : 'flex-wrap'}`} style={{ gap: 8 }}>
        {!narrow && canCreate && client.saveTemplate && <button type="button" onClick={saveAsTemplate} className={`${PILL} bg-[#1F1F1F] text-white hover:bg-[#2E2E2E]`} style={{ height: 44, padding: '0 18px', fontSize: 11, letterSpacing: '.14em' }}>SAVE AS TEMPLATE</button>}
        {!narrow && canEdit && !isLocked && <button type="button" disabled={saving} onClick={sendTest} className={`${PILL} bg-[#1F1F1F] text-white hover:bg-[#2E2E2E]`} style={{ height: 44, padding: '0 18px', fontSize: 11, letterSpacing: '.14em' }}>{testFresh ? 'TEST SENT ✓' : 'SEND TEST TO ME'}</button>}
        <button type="button" onClick={() => { setStep(3); setPane('edit'); }} className={`${PILL} bg-white text-black hover:opacity-85 ${narrow ? 'flex-1' : ''}`} style={{ height: 44, padding: '0 22px', fontSize: 11, letterSpacing: '.14em' }}>REVIEW &amp; SEND</button>
        {narrow && (
          <MoreMenu
            tone="dark"
            size={44}
            label="More"
            testId="studio-more"
            items={[
              canCreate && client.saveTemplate && { label: 'SAVE AS TEMPLATE', onClick: saveAsTemplate },
              canEdit && !isLocked && { label: testFresh ? 'TEST SENT ✓' : 'SEND TEST TO ME', onClick: sendTest, disabled: saving },
            ]}
          />
        )}
      </div>
    </header>
  );

  const stepBar = (
    <nav role="tablist" aria-label="Email steps" className="flex overflow-x-auto bg-white font-mono" style={{ gap: 6, padding: 6, borderRadius: 999, scrollbarWidth: 'none' }}>
      {STEPS.map((label, i) => {
        const act = step === i;
        const done = stepDone[i] && !act && i < 3;
        return (
          <button key={label} type="button" role="tab" aria-selected={act} onClick={() => setStep(i)} data-testid={`step-${i + 1}`}
            className={`flex items-center justify-center whitespace-nowrap rounded-full transition-colors duration-200 focus:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#000] ${act ? '' : 'hover:bg-[#F2F2F2]'}`}
            style={{ flex: '1 0 auto', height: 46, padding: '0 18px 0 8px', gap: 10, border: 'none', background: act ? '#0A0A0A' : 'transparent', color: act ? '#fff' : '#000', fontSize: 11, fontWeight: 800, letterSpacing: '.14em' }}>
            <span className="flex flex-none items-center justify-center" style={{ width: 30, height: 30, borderRadius: '50%', background: act ? '#fff' : '#F2F2F2', color: '#000', fontSize: 10 }}>{done ? '✓' : `0${i + 1}`}</span>
            {label}
          </button>
        );
      })}
    </nav>
  );

  const editor = (
    <div className="flex min-w-0 flex-col" style={{ gap: 14 }}>
      {step === 0 && (
        <DesignStep {...{
          isPage, readOnly, starter, pickStarter, templates, applyTemplate, theme, scheme, schemes, schemeId, setSchemeId: (v) => { setSchemeId(v); touch(); },
          showName, setShowName: (v) => { setShowName(v); touch(); }, logoUrl, replaceLogo: client.replaceLogo ? replaceLogo : null, palette, editScheme, addScheme, removeScheme, events,
          client: isPage ? client : null,
        }} />
      )}
      {step === 1 && (
        <WriteStep {...{
          meta, setMetaField, blocks, readOnly, activeKey, setActiveKey, setBlock, addBlock, moveBlock, duplicateBlock, deleteBlock, theme, scheme,
          events, eventMap, client, recentImages, setRecentImages, narrow,
        }} />
      )}
      {step === 2 && (
        isPage ? (
          <AudiencePicker ownerType={page.ownerType} ownerId={page.ownerId} channel="email" followerWord={page.followerWord} value={audience}
            onChange={(v) => { setAudience(v); touch(); }} readOnly={readOnly} onReach={setReach} />
        ) : (
          <AudiencePicker mode="admin" value={audience} onChange={(v) => { setAudience(v); touch(); }} readOnly={readOnly} onReach={setReach} />
        )
      )}
      {step === 3 && (
        <SendPanel
          channel="email" checks={checks} onFix={onFix} canSendNow={serverCanSend && !dirty} dirty={dirty} saving={saving}
          isLocked={isLocked} isScheduled={isScheduled} status={status} scheduledFor={scheduledFor}
          scheduleError={scheduleError} scheduleInput={scheduleInput} onScheduleInput={setScheduleInput}
          audienceCount={count} reach={reach} budgetLine={`${totalWords.toLocaleString()} WORDS · GMAIL CLIPS AT 1,500`}
          perms={perms} isNew={isNew} noun={isPage ? 'PEOPLE' : 'USERS'} testTarget={testTarget} testSentLabel={testFresh ? 'TEST SENT ✓' : 'SEND TEST TO ME'}
          onSave={save} onTest={sendTest} onSend={doSend} onSchedule={doSchedule} onUnschedule={doUnschedule} onDuplicate={doDuplicate}
        />
      )}
      <div className="flex justify-between" style={{ gap: 8 }}>
        <button type="button" onClick={() => setStep(Math.max(0, step - 1))} className={`${PILL} bg-white hover:bg-[#EDEDED]`} style={{ height: 46, padding: '0 20px', fontSize: 11, letterSpacing: '.14em', visibility: step === 0 ? 'hidden' : 'visible' }}>← BACK</button>
        {step < 3 && <button type="button" onClick={() => setStep(step + 1)} className={`${PILL} bg-[#0A0A0A] text-white hover:opacity-85`} style={{ height: 46, padding: '0 22px', fontSize: 11, letterSpacing: '.14em' }} data-testid="step-next">{NEXT_LABEL[step]}</button>}
      </div>
    </div>
  );

  const preview = (
    <aside className="flex min-w-0 flex-col bg-white font-mono" style={{ gap: 10, borderRadius: 28, padding: 14, position: wide ? 'sticky' : 'static', top: 14 }} data-testid="studio-preview">
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 8, padding: '4px 6px 0' }}>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.18em' }}>PREVIEW</span>
        <div className="flex" style={{ gap: 4, padding: 4, borderRadius: 999, background: '#F2F2F2' }} role="radiogroup" aria-label="Preview width">
          {[['desktop', 'DESKTOP'], ['phone', 'PHONE']].map(([v, l]) => (
            <button key={v} type="button" role="radio" aria-checked={view === v} onClick={() => setView(v)} style={{ height: 30, padding: '0 14px', border: 'none', borderRadius: 999, background: view === v ? '#0A0A0A' : 'transparent', color: view === v ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 10, fontWeight: 800, letterSpacing: '.12em' }}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex items-center" style={{ gap: 12, padding: '10px 14px 10px 10px', borderRadius: 18, background: '#F5F5F5' }} data-testid="inbox-line">
        <span aria-hidden="true" className="flex-none overflow-hidden" style={{ width: 36, height: 36, borderRadius: '50%', background: '#0A0A0A' }}>
          {isPage ? (logoUrl && <img src={logoUrl} alt="" className="h-full w-full object-cover" />) : <img src="/portal-mark-180.png" alt="" className="h-full w-full object-contain" style={{ padding: 4 }} />}
        </span>
        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
          <div className="flex justify-between" style={{ gap: 8, fontSize: 11 }}><b className="truncate">{isPage ? `${page?.name} via Portal` : 'Portal'}</b><span className="flex-none" style={{ color: '#9CA3AF' }}>NOW</span></div>
          <span className="truncate" style={{ fontSize: 11, fontWeight: 700, fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>{meta.subject || '(no subject)'}</span>
          <span className="truncate" style={{ fontSize: 11, color: '#6B7280', fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>{meta.preview || '(no preview text)'}</span>
        </div>
      </div>
      <div style={{ borderRadius: 20, overflow: 'hidden', background: theme.canvas, zoom }}>
        <EmailPreview meta={meta} blocks={blocks} page={previewPage} scheme={scheme} showName={showName} phone={phone} events={eventMap} activeKey={activeKey} onSelect={selectFromPreview} />
      </div>
    </aside>
  );

  // Only a phone gets the switch: a tablet has the room to stack the editor and the preview.
  const paneSwitch = narrow && (
    <div className="flex bg-white font-mono" style={{ gap: 4, padding: 4, borderRadius: 999 }} role="tablist" aria-label="Editor or preview" data-testid="pane-switch">
      {[['edit', 'EDIT'], ['preview', 'PREVIEW']].map(([v, l]) => (
        <button key={v} type="button" role="tab" aria-selected={pane === v} onClick={() => setPane(v)} data-testid={`pane-${v}`}
          className={`${PILL} flex-1`} style={{ height: 40, fontSize: 11, letterSpacing: '.14em', ...pillOf(pane === v) }}>{l}</button>
      ))}
    </div>
  );

  return (
    <NarrowCtx.Provider value={narrow}>
    <div ref={rootRef} className="flex w-full flex-col font-mono text-black" style={{ gap: 14, overflowX: 'clip' }} data-testid="email-builder">
      {(onBack || roleTag) && (
        <div className="flex items-center justify-between" style={{ gap: 10 }}>
          {onBack ? (
            <button type="button" onClick={onBack} className={`${PILL} bg-white hover:bg-[#EDEDED]`} style={{ height: 42, padding: '0 18px', gap: 10, fontSize: 11, letterSpacing: '.16em' }}>
              <Icon d={ICON.back} />ALL SENDS
            </button>
          ) : <span />}
          {roleTag && <span className="flex items-center" style={{ height: 30, padding: '0 14px', borderRadius: 999, background: '#22ECFB', fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>{roleTag}</span>}
        </div>
      )}
      {header}
      {paneSwitch}
      {(!narrow || pane === 'edit') && stepBar}
      <div className="grid items-start" style={{ gap: 14, gridTemplateColumns: wide ? `minmax(0,1fr) ${previewCol}px` : 'minmax(0,1fr)' }}>
        {(!narrow || pane === 'edit') && editor}
        {(!narrow || pane === 'preview') && preview}
      </div>
    </div>
    </NarrowCtx.Provider>
  );
}

function Icon({ d, size = 14 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
}

/** The shared studio card, told whether the studio is on a phone. */
function Card(props) {
  const narrow = useContext(NarrowCtx);
  return <StudioCard narrow={narrow} {...props} />;
}

function Warn({ children }) {
  return <span style={{ padding: '10px 14px', borderRadius: 14, background: '#FEF3C7', fontSize: 10, lineHeight: 1.6, letterSpacing: '.04em' }} role="note">{children}</span>;
}

// ── 01 DESIGN ───────────────────────────────────────────────────────────────

function DesignStep({
  isPage, readOnly, starter, pickStarter, templates, applyTemplate, theme, scheme, schemes, schemeId, setSchemeId,
  showName, setShowName, logoUrl, replaceLogo, palette, editScheme, addScheme, removeScheme, events, client,
}) {
  const logoInput = useRef(null);
  return (
    <>
      <Card title="START FROM" note="PICKING ONE REPLACES THE BLOCKS" testId="start-from">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
          {STARTERS.map((st) => {
            const act = starter === st.id;
            const mini = buildStarter(st.id, events).slice(0, 4);
            return (
              <button key={st.id} type="button" disabled={readOnly} onClick={() => pickStarter(st.id)} aria-pressed={act} data-testid={`starter-${st.id}`}
                className="flex flex-col text-left transition-colors duration-200 hover:bg-[#EDEDED] focus:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#000]"
                style={{ gap: 12, padding: 12, border: 'none', borderRadius: 22, background: act ? '#F2F2F2' : '#FAFAFA', boxShadow: act ? 'inset 0 0 0 2px #0A0A0A' : 'none', fontFamily: 'inherit' }}>
                <div className="flex flex-col overflow-hidden" style={{ height: 112, borderRadius: 14, background: theme.canvas, padding: 10, gap: 5, boxSizing: 'border-box' }}>
                  <span style={{ height: 30, flexShrink: 0, borderRadius: 8, background: theme.primary }} />
                  {mini.map((b) => (
                    <span key={b.key} style={{
                      flexShrink: 0, borderRadius: 6,
                      height: b.type === 'band' ? 14 : b.type === 'button' ? 10 : b.type === 'event' || b.type === 'imgtext' ? 22 : 16,
                      background: b.type === 'band' ? (b.tone === 'accent' ? theme.accent : theme.primary) : b.type === 'button' ? theme.primary : '#fff',
                    }} />
                  ))}
                </div>
                <div className="flex flex-col" style={{ gap: 4, padding: '0 4px 2px' }}>
                  <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.14em' }}>{st.name}</span>
                  <span style={{ fontSize: 10, lineHeight: 1.5, color: '#6B7280' }}>{st.note}</span>
                </div>
              </button>
            );
          })}
        </div>
        {templates.length > 0 && (
          <div className="flex flex-col" style={{ gap: 10 }} data-testid="your-templates">
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: '#6B7280' }}>YOUR TEMPLATES</span>
            <div className="flex flex-wrap" style={{ gap: 8 }}>
              {templates.map((t) => (
                <button key={t.id} type="button" disabled={readOnly} onClick={() => applyTemplate(t)} className={`${PILL} bg-[#F2F2F2] hover:bg-[#E5E5E5]`} style={{ height: 38, padding: '0 16px', fontSize: 10, letterSpacing: '.12em' }}>
                  {t.name} · {(t.blocks || []).length} BLOCKS
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card title="YOUR BRAND" note={isPage ? 'DEFAULT IS PORTAL. ADD UP TO 3 OF YOUR OWN.' : 'PORTAL EMAILS USE PORTAL COLORS'} testId="your-brand" gap={20}>
        {isPage && (
          <div className="flex flex-wrap items-center" style={{ gap: 14 }}>
            <span aria-hidden="true" className="flex-none overflow-hidden" style={{ width: 64, height: 64, borderRadius: 18, background: '#F2F2F2' }}>
              {logoUrl && <img src={logoUrl} alt="" className="h-full w-full object-cover" />}
            </span>
            <div className="flex min-w-0 flex-col" style={{ flex: '1 1 200px', gap: 6 }}>
              <Segmented label="TOP OF THE EMAIL" track="transparent" readOnly={readOnly} value={showName} onChange={setShowName}
                options={[{ value: true, label: 'LOGO + NAME' }, { value: false, label: 'LOGO ONLY' }]} />
            </div>
            {replaceLogo && !readOnly && (
              <>
                <button type="button" onClick={() => logoInput.current?.click()} className={`${PILL} bg-[#F2F2F2] hover:bg-[#E5E5E5]`} style={{ height: 36, padding: '0 14px', fontSize: 10, letterSpacing: '.12em' }}>REPLACE LOGO</button>
                <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={(e) => { replaceLogo(e.target.files?.[0]); e.target.value = ''; }} />
              </>
            )}
          </div>
        )}
        <div className="flex flex-col" style={{ gap: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: '#6B7280' }}>COLOR SCHEME</span>
          <div className="flex flex-wrap" style={{ gap: 8 }} role="radiogroup" aria-label="Color scheme">
            <SchemeChip on={!schemeId} name="PORTAL" portal onClick={() => setSchemeId(null)} readOnly={readOnly} />
            {isPage && schemes.map((s) => <SchemeChip key={s.id} on={schemeId === s.id} name={s.name} scheme={s} onClick={() => setSchemeId(s.id)} readOnly={readOnly} />)}
            {isPage && !readOnly && schemes.length < 3 && (
              <button type="button" onClick={addScheme} data-testid="new-scheme" className="rounded-full font-extrabold hover:border-black hover:text-black"
                style={{ height: 52, padding: '0 20px', border: '2px dashed #CFCFCF', background: 'transparent', fontFamily: 'inherit', fontSize: 11, letterSpacing: '.14em', color: '#6B7280' }}>+ NEW SCHEME</button>
            )}
          </div>
        </div>
        {isPage && scheme && <SchemeEditor scheme={scheme} palette={palette} readOnly={readOnly} onEdit={(patch) => editScheme(scheme.id, patch)} onDelete={() => removeScheme(scheme)} />}
        {isPage && client?.getEmailSettings && <SendingFrom client={client} readOnly={readOnly} />}
      </Card>
    </>
  );
}

function SchemeChip({ on, name, scheme, portal, onClick, readOnly }) {
  return (
    <button type="button" role="radio" aria-checked={on} disabled={readOnly} onClick={onClick} data-testid={`scheme-${portal ? 'portal' : scheme.id}`}
      className="flex items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#000]"
      style={{ height: 52, padding: '0 18px 0 8px', gap: 12, border: 'none', ...pillOf(on), fontFamily: 'inherit', fontSize: 11, fontWeight: 800, letterSpacing: '.14em' }}>
      {portal ? (
        <span aria-hidden="true" style={{ width: 36, height: 36, boxSizing: 'border-box', borderRadius: '50%', border: '3px solid transparent', background: `linear-gradient(#0A0A0A,#0A0A0A) padding-box, ${RING} border-box` }} />
      ) : (
        <span aria-hidden="true" className="flex">
          <span style={{ width: 22, height: 36, borderRadius: '18px 0 0 18px', background: scheme.primary }} />
          <span style={{ width: 14, height: 36, background: scheme.accent }} />
          <span style={{ width: 14, height: 36, borderRadius: '0 18px 18px 0', background: scheme.canvas, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.08)' }} />
        </span>
      )}
      {name}
    </button>
  );
}

function SchemeEditor({ scheme, palette, readOnly, onEdit, onDelete }) {
  const [draft, setDraft] = useState({});
  useEffect(() => { setDraft({}); }, [scheme.id]);
  const warn = schemeWarning(scheme);
  const rows = [['primary', 'PRIMARY', 'Top card, buttons'], ['accent', 'ACCENT', 'Label ring, bands'], ['canvas', 'BACKGROUND', 'Behind everything']];
  const commit = (k, v) => {
    const hex = normalizeHex(v);
    setDraft((d) => ({ ...d, [k]: v }));
    if (hex) onEdit({ [k]: hex });
  };
  return (
    <div className="flex flex-col" style={{ gap: 14, padding: 18, borderRadius: 22, background: '#F5F5F5' }} data-testid="scheme-editor">
      <div className="flex items-center" style={{ gap: 10 }}>
        <input value={scheme.name} readOnly={readOnly} onChange={(e) => onEdit({ name: e.target.value.toUpperCase().slice(0, 40) })} aria-label="Scheme name"
          className="min-w-0 flex-1 rounded-[12px] border-2 border-transparent bg-white font-mono font-extrabold uppercase outline-none focus:border-black" style={{ height: 40, padding: '0 14px', fontSize: 12, letterSpacing: '.14em' }} />
        {!readOnly && <button type="button" onClick={onDelete} className="rounded-full font-extrabold" style={{ height: 40, padding: '0 14px', border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 10, letterSpacing: '.12em', color: '#EF4444' }}>DELETE</button>}
      </div>
      {rows.map(([k, label, use]) => (
        <div key={k} className="flex flex-wrap items-center" style={{ gap: 12 }}>
          <label className="relative flex-none cursor-pointer" style={{ width: 44, height: 44, borderRadius: '50%', background: scheme[k], boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.12)' }} aria-label={scheme[k]}>
            <input type="color" value={scheme[k]} disabled={readOnly} onChange={(e) => commit(k, e.target.value)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" aria-label={`${label} color`} />
          </label>
          <div className="flex flex-col" style={{ width: 118, gap: 2 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>{label}</span>
            <span style={{ fontSize: 9, letterSpacing: '.06em', color: '#6B7280' }}>{use}</span>
          </div>
          <input value={draft[k] ?? scheme[k]} readOnly={readOnly} onChange={(e) => commit(k, e.target.value.startsWith('#') ? e.target.value.slice(0, 7) : `#${e.target.value.slice(0, 6)}`)}
            aria-label={`${label} hex`} className="rounded-[10px] border-2 border-transparent bg-white font-mono uppercase outline-none focus:border-black" style={{ width: 96, height: 36, padding: '0 12px', fontSize: 11 }} />
          <div className="flex flex-wrap" style={{ gap: 6 }}>
            {palette.map((c) => (
              <button key={c} type="button" disabled={readOnly} onClick={() => commit(k, c)} title={c} aria-label={c}
                style={{ width: 24, height: 24, border: 'none', borderRadius: '50%', background: c, boxShadow: String(scheme[k]).toLowerCase() === c.toLowerCase() ? '0 0 0 2px #fff, 0 0 0 4px #0A0A0A' : 'inset 0 0 0 1px rgba(0,0,0,.12)' }} />
            ))}
          </div>
        </div>
      ))}
      <span style={{ fontSize: 10, lineHeight: 1.6, color: '#6B7280' }}>Swatches come from your logo. Text on each color flips to black or white on its own.</span>
      {warn && <Warn>{warn}</Warn>}
    </div>
  );
}

// ── 02 WRITE ────────────────────────────────────────────────────────────────

function WriteStep({
  meta, setMetaField, blocks, readOnly, activeKey, setActiveKey, setBlock, addBlock, moveBlock, duplicateBlock, deleteBlock, theme,
  events, eventMap, client, recentImages, setRecentImages, narrow,
}) {
  const twoCol = narrow ? '1fr' : '1fr 1fr';
  const len = (meta.preview || '').length;
  const f = (k) => ({ value: meta[k], readOnly, onChange: (v) => setMetaField(k, v) });
  return (
    <>
      <Card title="INBOX + TOP" testId="email-meta" gap={16}>
        <div className="grid" style={{ gridTemplateColumns: twoCol, gap: 14 }}>
          <SoftField label="SUBJECT" required right="SENTENCE CASE"><SoftText {...f('subject')} placeholder="Next Thursday, doors at eight" ariaLabel="Subject" /></SoftField>
          <SoftField label="PREVIEW TEXT" required right={`${len}/85`} rightColor={len > 90 ? '#EF4444' : '#9CA3AF'}><SoftText {...f('preview')} placeholder="Say something the subject doesn't." ariaLabel="Preview text" /></SoftField>
          <SoftField label="LABEL"><SoftText {...f('issueLabel')} caps placeholder="OCTOBER" ariaLabel="Label" /></SoftField>
          <SoftField label="HEADLINE" right="BLANK = SUBJECT"><SoftText {...f('title')} caps placeholder={(meta.subject || 'HEADLINE').toUpperCase()} ariaLabel="Headline" /></SoftField>
        </div>
        <SoftField label="INTRO"><SoftText {...f('intro')} rows={2} placeholder="One or two sentences under the headline." ariaLabel="Intro" /></SoftField>
      </Card>

      <Card title="BLOCKS" note="TAP A BLOCK HERE OR IN THE PREVIEW" testId="email-sections" gap={12}>
        {blocks.map((b, i) => (
          <BlockRow key={b.key} b={b} i={i} total={blocks.length} open={activeKey === b.key} readOnly={readOnly} theme={theme}
            onOpen={() => setActiveKey(activeKey === b.key ? null : b.key)} onChange={(patch) => setBlock(b.key, patch)}
            onMove={(d) => moveBlock(i, d)} onDuplicate={() => duplicateBlock(i)} onDelete={() => deleteBlock(b.key)}
            events={events} event={eventMap[b.eventId]} client={client} recentImages={recentImages} setRecentImages={setRecentImages} twoCol={twoCol} />
        ))}
        {blocks.length === 0 && <div style={{ padding: '28px 12px', textAlign: 'center', fontSize: 11, fontWeight: 800, letterSpacing: '.14em', color: '#9CA3AF' }}>{readOnly ? 'NO BLOCKS.' : 'NO BLOCKS YET. ADD ONE BELOW.'}</div>}
        {!readOnly && (
          <div className="flex flex-col" style={{ gap: 10, paddingTop: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: '#6B7280' }}>ADD A BLOCK</span>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
              {ADD_ORDER.map((t) => (
                <button key={t} type="button" onClick={() => addBlock(t)} data-testid={`add-${t}`}
                  className="group flex items-center rounded-full bg-[#F2F2F2] text-left transition-colors duration-200 hover:bg-[#0A0A0A] hover:text-white focus:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#000]"
                  style={{ height: 52, padding: '0 12px 0 8px', gap: 10, border: 'none', fontFamily: 'inherit', fontSize: 10, fontWeight: 800, letterSpacing: '.12em' }}>
                  <span className="flex flex-none items-center justify-center bg-white text-black" style={{ width: 36, height: 36, borderRadius: '50%', fontSize: 12 }}>+</span>
                  {BLOCK_TYPES[t].label}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card title="BOTTOM BUTTON" testId="bottom-button" gap={14}>
        <div className="grid" style={{ gridTemplateColumns: twoCol, gap: 14 }}>
          <SoftField label="LABEL" right="BLANK = NO BUTTON"><SoftText {...f('ctaText')} caps placeholder="OPEN APP" ariaLabel="Bottom button label" /></SoftField>
          <SoftField label="GOES TO"><SoftText {...f('ctaUrl')} placeholder="https://heyportal.app/" ariaLabel="Bottom button link" /></SoftField>
        </div>
      </Card>
    </>
  );
}

function BlockRow({ b, i, total, open, readOnly, theme, onOpen, onChange, onMove, onDuplicate, onDelete, events, event, client, recentImages, setRecentImages, twoCol }) {
  const narrow = useContext(NarrowCtx);
  const T = BLOCK_TYPES[b.type];
  const thumb = T.hasImage ? b.img : b.type === 'event' ? event?.image : null;
  const bandColor = b.type === 'band' ? bandBg(b.tone, theme) : null;
  const ctl = [
    { label: 'Move up', d: ICON.up, off: i === 0, fg: i === 0 ? '#D1D5DB' : '#4B5563', on: () => onMove(-1) },
    { label: 'Move down', d: ICON.down, off: i === total - 1, fg: i === total - 1 ? '#D1D5DB' : '#4B5563', on: () => onMove(1) },
    { label: 'Duplicate', d: ICON.dup, off: false, fg: '#4B5563', on: onDuplicate },
    { label: 'Delete', d: ICON.del, off: false, fg: '#EF4444', on: onDelete },
  ];
  return (
    <div style={{ borderRadius: 22, background: open ? '#F2F2F2' : '#F7F7F7', boxShadow: open ? 'inset 0 0 0 2px #0A0A0A' : 'none', transition: 'background-color .2s' }} data-testid={`block-${b.type}`} data-open={open ? 'true' : undefined}>
      <div className="flex items-center" style={{ gap: 10, padding: '8px 10px 8px 8px' }}>
        <button type="button" onClick={onOpen} aria-expanded={open} className="flex min-w-0 flex-1 items-center text-left" style={{ gap: 12, padding: 0, border: 'none', background: 'transparent', fontFamily: 'inherit' }}>
          {thumb
            ? <span aria-hidden="true" className="flex-none" style={{ width: 40, height: 40, borderRadius: 12, background: `#EDEDED center/cover no-repeat url(${JSON.stringify(thumb)})` }} />
            : <span aria-hidden="true" className="flex flex-none items-center justify-center" style={{ width: 40, height: 40, borderRadius: 12, background: bandColor || '#fff', color: bandColor ? bandFg('auto', bandColor, theme) : '#000', fontSize: 10, fontWeight: 800 }}>{T.mark}</span>}
          <span className="flex min-w-0 flex-col" style={{ gap: 3 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>{T.label}</span>
            <span className="truncate" style={{ fontSize: 11, color: '#6B7280' }}>{excerptOf(b, event?.name)}</span>
          </span>
        </button>
        {needsAlt(b) && <span className="flex items-center whitespace-nowrap" style={{ height: 22, padding: '0 9px', borderRadius: 999, background: '#FEE2E2', color: '#B91C1C', fontSize: 9, fontWeight: 800, letterSpacing: '.12em' }}>NEEDS ALT</span>}
        {!readOnly && narrow && (
          <MoreMenu
            size={36}
            label={`${T.label} block actions`}
            items={ctl.map((c) => ({ label: c.label.toUpperCase(), onClick: c.on, disabled: c.off, variant: c.label === 'Delete' ? 'danger' : undefined }))}
          />
        )}
        {!readOnly && !narrow && (
          <div className="flex flex-none" style={{ gap: 2 }}>
            {ctl.map((c) => (
              <button key={c.label} type="button" aria-label={c.label} title={c.label} disabled={c.off} onClick={c.on}
                className="flex items-center justify-center rounded-full hover:bg-white disabled:hover:bg-transparent" style={{ width: 32, height: 32, border: 'none', background: 'transparent', color: c.fg }}>
                <Icon d={c.d} />
              </button>
            ))}
          </div>
        )}
      </div>
      {open && (
        <div className="flex flex-col" style={{ padding: narrow ? '4px 10px 14px' : '6px 16px 18px', gap: 14 }}>
          <BlockEditor b={b} readOnly={readOnly} theme={theme} onChange={onChange} events={events} client={client} recentImages={recentImages} setRecentImages={setRecentImages} twoCol={twoCol} />
        </div>
      )}
    </div>
  );
}

function BlockEditor({ b, readOnly, theme, onChange, events, client, recentImages, setRecentImages, twoCol }) {
  const t = (k, extra = {}) => ({ value: b[k], readOnly, inBlock: true, onChange: (v) => onChange({ [k]: v }), ...extra });
  const link = (labelKey, label, ph) => (
    <div className="grid" style={{ gridTemplateColumns: twoCol, gap: 10 }}>
      <SoftField label={label}><SoftText {...t(labelKey, { caps: true, placeholder: ph, ariaLabel: label })} /></SoftField>
      <SoftField label="GOES TO"><SoftText {...t('url', { placeholder: 'https://heyportal.app/…', ariaLabel: 'Goes to' })} /></SoftField>
    </div>
  );
  switch (b.type) {
    case 'text':
      return (
        <>
          <SoftField label="HEADING"><SoftText {...t('heading', { ariaLabel: 'Heading' })} /></SoftField>
          <SoftField label="BODY · PLAIN TEXT"><SoftText {...t('body', { rows: 4, ariaLabel: 'Body' })} /></SoftField>
        </>
      );
    case 'imgtext':
      return (
        <>
          <ImagePicker b={b} readOnly={readOnly} onChange={onChange} client={client} recentImages={recentImages} setRecentImages={setRecentImages} />
          <SoftField label="ALT TEXT" required right="SHOWS WHEN IMAGES ARE OFF"><SoftText {...t('alt', { placeholder: 'A backyard event at dusk', ariaLabel: 'Alt text' })} /></SoftField>
          <Segmented label="IMAGE ON" readOnly={readOnly} value={b.side} onChange={(v) => onChange({ side: v })} options={[{ value: 'left', label: 'LEFT' }, { value: 'right', label: 'RIGHT' }]} />
          <SoftField label="HEADING"><SoftText {...t('heading', { ariaLabel: 'Heading' })} /></SoftField>
          <SoftField label="BODY · PLAIN TEXT"><SoftText {...t('body', { rows: 4, ariaLabel: 'Body' })} /></SoftField>
          {link('link', 'LINK LABEL', 'READ MORE')}
        </>
      );
    case 'photo':
      return (
        <>
          <ImagePicker b={b} readOnly={readOnly} onChange={onChange} client={client} recentImages={recentImages} setRecentImages={setRecentImages} />
          <SoftField label="ALT TEXT" required right="SHOWS WHEN IMAGES ARE OFF"><SoftText {...t('alt', { placeholder: 'The long table set for dinner', ariaLabel: 'Alt text' })} /></SoftField>
          <SoftField label="CAPTION · OPTIONAL"><SoftText {...t('caption', { ariaLabel: 'Caption' })} /></SoftField>
        </>
      );
    case 'band': {
      const bg = bandBg(b.tone, theme);
      const inkMap = { white: '#FFFFFF', black: '#0A0A0A', accent: theme.accent, primary: theme.primary };
      const hard = b.ink && b.ink !== 'auto' && contrast(bg, bandFg(b.ink, bg, theme)) < 3;
      return (
        <>
          <Segmented label="COLOR" readOnly={readOnly} value={b.tone} onChange={(v) => onChange({ tone: v })}
            options={[{ value: 'primary', label: 'PRIMARY', dot: theme.primary }, { value: 'accent', label: 'ACCENT', dot: theme.accent }, { value: 'black', label: 'BLACK', dot: '#0A0A0A' }]} />
          <Segmented label="TEXT COLOR" readOnly={readOnly} value={b.ink || 'auto'} onChange={(v) => onChange({ ink: v })}
            options={[{ value: 'auto', label: 'AUTO' }, ...['white', 'black', 'accent', 'primary'].map((k) => ({ value: k, label: k.toUpperCase(), dot: inkMap[k] }))]} />
          {hard && <Warn>Hard to read on this band. Pick another text color or AUTO.</Warn>}
          <SoftField label="BAND TEXT"><SoftText {...t('heading', { caps: true, ariaLabel: 'Band text' })} /></SoftField>
          <SoftField label="SECOND LINE · OPTIONAL"><SoftText {...t('body', { ariaLabel: 'Second line' })} /></SoftField>
        </>
      );
    }
    case 'event':
      return (
        <>
          <div className="flex flex-col" style={{ gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: '#6B7280' }}>PICK ONE OF YOUR EVENTS</span>
            {events.length === 0 && <span style={{ fontSize: 11, color: '#6B7280' }}>No upcoming events to pick from yet.</span>}
            {events.map((ev) => {
              const on = b.eventId === ev.id;
              return (
                <button key={ev.id} type="button" disabled={readOnly} onClick={() => onChange({ eventId: ev.id })} aria-pressed={on}
                  className="flex items-center text-left" style={{ gap: 12, padding: '6px 14px 6px 6px', border: 'none', borderRadius: 18, background: on ? '#0A0A0A' : '#fff', color: on ? '#fff' : '#000', fontFamily: 'inherit' }}>
                  <span aria-hidden="true" className="flex-none" style={{ width: 40, height: 40, borderRadius: 12, background: ev.image ? `#EDEDED center/cover no-repeat url(${JSON.stringify(ev.image)})` : '#EDEDED' }} />
                  <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                    <span className="truncate" style={{ fontSize: 12, fontWeight: 700 }}>{ev.name}</span>
                    <span style={{ fontSize: 9, letterSpacing: '.12em', opacity: 0.7 }}>{ev.when}</span>
                  </span>
                </button>
              );
            })}
            <span style={{ fontSize: 10, color: '#6B7280' }}>Date, place, price and capacity fill in from the event and stay current until you send.</span>
          </div>
          {link('cta', 'BUTTON LABEL', 'GET TICKETS')}
        </>
      );
    case 'button':
      return (
        <>
          <Segmented label="STYLE" readOnly={readOnly} value={b.style} onChange={(v) => onChange({ style: v })} options={[{ value: 'filled', label: 'FILLED' }, { value: 'outline', label: 'OUTLINE' }]} />
          {link('label', 'BUTTON LABEL', 'OPEN')}
        </>
      );
    default:
      return null;
  }
}

function ImagePicker({ b, readOnly, onChange, client, recentImages, setRecentImages }) {
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [soft, setSoft] = useState(null);
  const choices = Array.from(new Set([b.img, ...recentImages].filter(Boolean))).slice(0, 8);
  const upload = async (file) => {
    if (!file) return;
    setError(null); setSoft(null); setUploading(true);
    const r = await client.uploadImage(file);
    setUploading(false);
    if (!r?.success) { setError(r?.message || r?.error || 'Upload failed.'); return; }
    onChange({ img: r.data.url });
    setRecentImages((prev) => [r.data.url, ...prev.filter((u) => u !== r.data.url)]);
    // Email clients cache what they fetch for months, so a soft image is soft permanently.
    if (r.data.belowMinimum) setSoft(`That image is ${r.data.width}px wide, under the ${r.data.minimumWidth}px minimum. It will look soft on retina screens.`);
  };
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: '#6B7280' }}>IMAGE · ANY SHAPE, 440PX+ WIDE</span>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {choices.map((src) => (
          <button key={src} type="button" disabled={readOnly} onClick={() => onChange({ img: src })} aria-label="Use this image"
            style={{ width: 56, height: 56, padding: 0, border: 'none', borderRadius: 14, background: `#EDEDED center/cover no-repeat url(${JSON.stringify(src)})`, boxShadow: b.img === src ? '0 0 0 2px #fff, 0 0 0 4px #0A0A0A' : 'none' }} />
        ))}
        {!readOnly && (
          <button type="button" disabled={uploading} onClick={() => input.current?.click()} className="hover:border-black hover:text-black"
            style={{ width: 56, height: 56, border: '2px dashed #CFCFCF', borderRadius: 14, background: '#fff', fontFamily: 'inherit', fontSize: 9, fontWeight: 800, letterSpacing: '.1em', color: '#6B7280' }}>
            {uploading ? '…' : 'UPLOAD'}
          </button>
        )}
        <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} aria-label="Upload image" />
      </div>
      {error && <span style={{ fontSize: 11, color: '#EF4444' }}>{error}</span>}
      {soft && <Warn>{soft}</Warn>}
    </div>
  );
}
